import { CompensationMethod } from "@redotech/redo-return-core-common/compensation-method";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { RefundDutiesMode } from "../refund-duties-mode";
import type {
  OperationsSettingsPatch,
  ReturnsOperationsSettingsSource,
} from "./operations-settings-source";

export const CLOSE_SHOPIFY_RETURN_OPTIONS = [
  "enabled",
  "greenReturnsOnly",
  "disabled",
] as const;
export type CloseShopifyReturnOption =
  (typeof CLOSE_SHOPIFY_RETURN_OPTIONS)[number];

export const STORE_CREDIT_EXPIRATION_MIN_DAYS = 1;
export const STORE_CREDIT_EXPIRATION_MAX_DAYS = 3650;

export const shopifySettingsShape = {
  shopify: z.object({
    createShopifyReturns: z.boolean(),
    createShopifyReturnTypes: z.array(createEnum(CompensationMethod)),
    createShopifyReturnsForClaimsAndWarranties: z.boolean(),
    createSeparateShopifyReturnForGreenReturns: z.boolean(),
    createMultipleShopifyReturns: z.boolean(),
    addTrackingToShopifyReturns: z.boolean(),
    archiveShopifyOrdersAfterReturn: z.boolean(),
    closeShopifyReturnWhenProcessed: z.enum(CLOSE_SHOPIFY_RETURN_OPTIONS),
    disableRefundLineItems: z.boolean(),
    shouldAutoProcessRedoReturnWhenShopifyReturnCloses: z.boolean(),
    applyRestockingFeeToShopifyReturns: z.boolean(),
    /** Requires SET_SHOPIFY_RETURN_FEE_SYNC_SETTINGS. */
    syncReturnFeesToShopify: z.boolean(),
    refundAllReturnedItems: z.boolean(),
    skipExchangeRefunds: z.boolean(),
    showStoreCreditOnRefundNote: z.boolean(),
    refundDutiesMode: createEnum(RefundDutiesMode),
    dutyDrawbackEnabled: z.boolean(),
    dutyDrawbackEligibleCountries: z.array(z.string()),
    addOrderNotes: z.boolean(),
  }),
  /** Stored on `team.automation`, outside `settings`. */
  storeCreditExpiration: z.object({
    enabled: z.boolean(),
    days: z
      .number()
      .int()
      .min(STORE_CREDIT_EXPIRATION_MIN_DAYS)
      .max(STORE_CREDIT_EXPIRATION_MAX_DAYS),
  }),
};

export type ShopifySettings = z.infer<z.ZodObject<typeof shopifySettingsShape>>;

export function shopifySettingsFromTeam({
  settings,
  automation,
}: ReturnsOperationsSettingsSource): ShopifySettings {
  const { returns } = settings;
  return {
    shopify: {
      createShopifyReturns: !!settings.createShopifyReturns,
      createShopifyReturnTypes: settings.createShopifyReturnTypes ?? [],
      createShopifyReturnsForClaimsAndWarranties:
        !!settings.createShopifyReturnsForClaimsAndWarranties,
      createSeparateShopifyReturnForGreenReturns:
        !!settings.createSeparateShopifyReturnForGreenReturns,
      createMultipleShopifyReturns: !!settings.createMultipleShopifyReturns,
      addTrackingToShopifyReturns: settings.addTrackingToShopifyReturns ?? true,
      // The runtime treats an absent value as on.
      archiveShopifyOrdersAfterReturn:
        settings.archiveShopifyOrdersAfterReturn ?? true,
      closeShopifyReturnWhenProcessed:
        settings.closeShopifyReturnWhenProcessed ?? "enabled",
      disableRefundLineItems: settings.disableRefundLineItems ?? false,
      shouldAutoProcessRedoReturnWhenShopifyReturnCloses:
        returns?.shouldAutoProcessRedoReturnWhenShopifyReturnCloses ?? false,
      applyRestockingFeeToShopifyReturns:
        returns?.applyRestockingFeeToShopifyReturns ?? false,
      syncReturnFeesToShopify: returns?.syncReturnFeesToShopify ?? false,
      refundAllReturnedItems: returns?.refundAllReturnedItems ?? false,
      skipExchangeRefunds: returns?.skipExchangeRefunds ?? false,
      showStoreCreditOnRefundNote:
        returns?.showStoreCreditOnRefundNote ?? false,
      refundDutiesMode: returns?.refundDutiesMode ?? RefundDutiesMode.OFF,
      dutyDrawbackEnabled: returns?.dutyDrawback?.enabled ?? false,
      dutyDrawbackEligibleCountries:
        returns?.dutyDrawback?.eligibleCountries ?? [],
      addOrderNotes: settings.addOrderNotes ?? false,
    },
    storeCreditExpiration: {
      enabled: automation?.storeCreditExpiration?.enabled ?? false,
      days: automation?.storeCreditExpiration?.days || 60,
    },
  };
}

/** Store credit expiration lives on `team.automation`, so the aggregator writes it. */
export function shopifyTeamPatch(
  next: ShopifySettings,
): OperationsSettingsPatch {
  const { shopify } = next;
  return {
    set: {
      createShopifyReturns: shopify.createShopifyReturns,
      createShopifyReturnTypes: shopify.createShopifyReturnTypes,
      createShopifyReturnsForClaimsAndWarranties:
        shopify.createShopifyReturnsForClaimsAndWarranties,
      createSeparateShopifyReturnForGreenReturns:
        shopify.createSeparateShopifyReturnForGreenReturns,
      createMultipleShopifyReturns: shopify.createMultipleShopifyReturns,
      addTrackingToShopifyReturns: shopify.addTrackingToShopifyReturns,
      archiveShopifyOrdersAfterReturn: shopify.archiveShopifyOrdersAfterReturn,
      closeShopifyReturnWhenProcessed: shopify.closeShopifyReturnWhenProcessed,
      disableRefundLineItems: shopify.disableRefundLineItems,
      "returns.shouldAutoProcessRedoReturnWhenShopifyReturnCloses":
        shopify.shouldAutoProcessRedoReturnWhenShopifyReturnCloses,
      "returns.applyRestockingFeeToShopifyReturns":
        shopify.applyRestockingFeeToShopifyReturns,
      "returns.syncReturnFeesToShopify": shopify.syncReturnFeesToShopify,
      "returns.refundAllReturnedItems": shopify.refundAllReturnedItems,
      "returns.skipExchangeRefunds": shopify.skipExchangeRefunds,
      // Legacy stores false whenever refunds are not always created.
      "returns.showStoreCreditOnRefundNote":
        shopify.refundAllReturnedItems && shopify.showStoreCreditOnRefundNote,
      "returns.refundDutiesMode": shopify.refundDutiesMode,
      "returns.dutyDrawback.enabled": shopify.dutyDrawbackEnabled,
      "returns.dutyDrawback.eligibleCountries":
        shopify.dutyDrawbackEligibleCountries,
      addOrderNotes: shopify.addOrderNotes,
    },
    unset: [],
  };
}
